import React, { useState } from 'react';
import { McProfile, useGamingStore } from '../../../store/gamingStore';
import { Package, Lock, Trash2, Plus, Upload, Loader2 } from 'lucide-react';
import ModrinthSearchModal from '../ModrinthSearchModal';
import { serverAPI } from '../../../lib/api';

const api = (window as any).api;

interface Props {
  profile: McProfile;
  profileId: string;
}

const ProfileModsTab: React.FC<Props> = ({ profile, profileId }) => {
  const { syncServerProfile, isSyncingServerProfile } = useGamingStore();
  const [mods, setMods] = useState(profile.mods || []);
  const [showModrinth, setShowModrinth] = useState(false);
  const [removingFileName, setRemovingFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isServerOwner, setIsServerOwner] = useState<boolean | null>(null);

  const isServerProfile = profile.source === 'server';
  const ownerCanEdit = isServerOwner === true;
  const canManageMods = !isServerProfile || ownerCanEdit || profile.allow_user_mods;
  const isSyncing = isSyncingServerProfile === profileId;

  const requiredMods = isServerProfile && !ownerCanEdit ? mods.filter(m => m.required) : [];
  const optionalMods = isServerProfile && !ownerCanEdit ? mods.filter(m => !m.required) : mods;
  const installedVersionIds = mods.map((m: any) => m.version_id).filter(Boolean);

  const modSig = (arr: any[]) => arr
    .map((m: any) => `${m.file_name || ''}|${m.sha1 || ''}|${m.version_id || ''}|${m.required ? '1' : '0'}`)
    .sort()
    .join('||');
  const hasPendingChanges = modSig(mods as any) !== modSig((profile.mods || []) as any);

  React.useEffect(() => {
    let mounted = true;
    const checkOwner = async () => {
      if (!isServerProfile || !profile.erozion_server_id) {
        if (mounted) setIsServerOwner(false);
        return;
      }
      try {
        const userId = localStorage.getItem('user_id');
        const server = await serverAPI.getServer(profile.erozion_server_id);
        if (mounted) setIsServerOwner(!!server?.owner_id && !!userId && server.owner_id === userId);
      } catch {
        if (mounted) setIsServerOwner(false);
      }
    };
    checkOwner();
    return () => { mounted = false; };
  }, [isServerProfile, profile.erozion_server_id]);

  const handleSyncServerChanges = async () => {
    setError(null);
    const ok = await syncServerProfile(profileId, { mods });
    if (!ok) {
      setError('Échec de synchronisation vers le serveur. Vérifiez vos permissions owner.');
    }
  };

  const handleModInstalled = (mod: any) => {
    setMods(prev => {
      const filtered = prev.filter((m: any) => m.version_id !== mod.version_id && m.file_name !== mod.file_name);
      return [...filtered, mod];
    });
  };

  const handleRemoveMod = async (mod: any) => {
    setRemovingFileName(mod.file_name);
    setError(null);
    try {
      const res = await api.modrinthRemoveMod({ profileId, fileName: mod.file_name, versionId: mod.version_id });
      if (!res.success) throw new Error(res.error || 'Échec de la suppression');
      setMods(prev => prev.filter((m: any) => m.file_name !== mod.file_name));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRemovingFileName(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      {/* Header actions */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-text-primary">
          Mods ({mods.length})
        </h2>
        <div className="flex items-center gap-2">
          {isServerProfile && isServerOwner && hasPendingChanges && (
            <button
              onClick={handleSyncServerChanges}
              disabled={isSyncing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
            >
              {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
              Synchroniser les modifications
            </button>
          )}
          {canManageMods && (
            <>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-bg-secondary border border-border-color text-xs text-text-secondary hover:text-text-primary hover:border-gaming-accent/50 transition-colors">
              <Upload className="w-3.5 h-3.5" />
              Importer
            </button>
            <button
              onClick={() => setShowModrinth(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gaming-accent/10 border border-gaming-accent/30 text-xs text-gaming-accent hover:bg-gaming-accent/20 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Ajouter un mod
            </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">{error}</div>
      )}

      {mods.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-12 h-12 text-text-muted mx-auto mb-3" />
          <p className="text-sm text-text-secondary">Aucun mod installé</p>
          <p className="text-xs text-text-muted mt-1">Ajoutez des mods depuis Modrinth ou importez vos fichiers .jar</p>
          {canManageMods && (
            <button
              onClick={() => setShowModrinth(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gaming-accent/10 border border-gaming-accent/30 text-sm text-gaming-accent hover:bg-gaming-accent/20 transition-colors"
            >
              <Plus className="w-4 h-4" /> Parcourir Modrinth
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Required mods (server-locked) */}
          {requiredMods.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Mods requis du serveur
              </h3>
              <div className="space-y-1">
                {requiredMods.map((mod: any, i: number) => (
                  <div key={i} className="flex items-center justify-between bg-bg-secondary border border-border-color rounded-lg px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <Package className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-text-primary font-medium">{mod.name}</p>
                        <p className="text-[10px] text-text-muted">{mod.file_name} · {mod.source}</p>
                      </div>
                    </div>
                    <Lock className="w-3.5 h-3.5 text-text-muted" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optional / user mods */}
          {optionalMods.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                {isServerProfile && !ownerCanEdit ? 'Mods personnels' : 'Mods du profil'}
              </h3>
              <div className="space-y-1">
                {optionalMods.map((mod: any, i: number) => (
                  <div key={i} className="flex items-center justify-between bg-bg-secondary border border-border-color rounded-lg px-3 py-2 group">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                        <Package className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm text-text-primary font-medium">{mod.name}</p>
                        <p className="text-[10px] text-text-muted">{mod.file_name} · {mod.source}</p>
                      </div>
                    </div>
                    {canManageMods && (
                      <button
                        onClick={() => handleRemoveMod(mod)}
                        disabled={removingFileName === mod.file_name}
                        className="p-1 rounded text-text-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                      >
                        {removingFileName === mod.file_name
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />
                        }
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showModrinth && (
        <ModrinthSearchModal
          profileId={profileId}
          mcVersion={profile.minecraft?.version || '1.21.4'}
          loader={profile.minecraft?.loader || 'vanilla'}
          installedVersionIds={installedVersionIds}
          onClose={() => setShowModrinth(false)}
          onModInstalled={handleModInstalled}
        />
      )}
    </div>
  );
};

export default ProfileModsTab;

